import React, { useEffect, useMemo, useRef } from 'react'
import { Typography } from '@surecompanies/core_components'
import { Chart } from 'react-chartjs-2'
import type { ActiveElement, ChartEvent, TooltipItem } from 'chart.js'
import type { ErrorBreakdownItemDto } from '../../../../api/dto/get-payment-bot-dashboard.dto'
import {
  ERROR_BREAKDOWN_CHART_CHROME,
  ERROR_BREAKDOWN_MAX_BAR_THICKNESS,
  ERROR_BREAKDOWN_ROW_HEIGHT,
} from '../../shared/constants'
import { formatErrorCode, toErrorCodeFilter } from '../../shared/utils'
import './chartjsSetup'
import styles from './CarrierDetail.module.scss'

const { Text, Title } = Typography

interface Props {
  errorBreakdown: ErrorBreakdownItemDto[]
  // Toggles the error-code filter for the recent-failures table below.
  onToggleErrorCode: (code: string) => void
}

export function ErrorBreakdownChart({
  errorBreakdown,
  onToggleErrorCode,
}: Props) {
  const errorBreakdownChartData = useMemo(() => {
    if (!errorBreakdown.length) return null
    const labels = errorBreakdown.map((e) => formatErrorCode(e.errorCode))
    return {
      labels,
      datasets: [
        {
          type: 'bar' as const,
          label: 'Failed Count',
          data: errorBreakdown.map((e) => e.count - e.recoveredCount),
          backgroundColor: 'rgba(239, 68, 68, 0.75)',
          maxBarThickness: ERROR_BREAKDOWN_MAX_BAR_THICKNESS,
          stack: 'failures',
        },
        {
          type: 'bar' as const,
          label: '2nd+ Attempts Success',
          data: errorBreakdown.map((e) => e.recoveredCount),
          backgroundColor: 'rgba(34, 197, 94, 0.75)',
          maxBarThickness: ERROR_BREAKDOWN_MAX_BAR_THICKNESS,
          stack: 'failures',
        },
      ],
    }
  }, [errorBreakdown])

  const errorBreakdownChartHeight = useMemo(
    () =>
      errorBreakdown.length * ERROR_BREAKDOWN_ROW_HEIGHT +
      ERROR_BREAKDOWN_CHART_CHROME,
    [errorBreakdown.length]
  )

  // Keep a ref so the onClick closure always reads the latest breakdown without
  // needing errorBreakdown in the options dep array. Without this, every
  // background refetch that returns a new array reference would re-create the
  // options object and trigger a full Chart.js re-initialisation (visible flash).
  const errorBreakdownRef = useRef(errorBreakdown)
  useEffect(() => {
    errorBreakdownRef.current = errorBreakdown
  }, [errorBreakdown])

  const errorBreakdownChartOptions = useMemo(
    () => ({
      indexAxis: 'y' as const,
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: true, position: 'bottom' as const },
        title: { display: false },
        tooltip: {
          callbacks: {
            footer: (items) => {
              const total = items.reduce(
                (sum: number, item: TooltipItem<'bar'>) =>
                  sum + (item.parsed.x ?? 0),
                0
              )
              return `Total: ${total}`
            },
          },
        },
      },
      onClick: (_event: ChartEvent, elements: ActiveElement[]) => {
        if (elements.length === 0) return
        const { index } = elements[0]
        const item = errorBreakdownRef.current[index]
        if (!item) return
        // Normalize codeless groups to 'UNKNOWN' so the bar is still clickable.
        onToggleErrorCode(toErrorCodeFilter(item.errorCode))
      },
      // Pointer cursor over the clickable bars so users know they can filter.
      onHover: (event: ChartEvent, elements: ActiveElement[]) => {
        const target = event.native?.target
        if (target instanceof HTMLElement) {
          target.style.cursor = elements.length > 0 ? 'pointer' : 'default'
        }
      },
      // Horizontal bar (indexAxis 'y'): resolve the clicked bar by its vertical
      // position. Interaction axis defaults to 'x', which measures horizontal
      // distance only and ignores which row was clicked — so clicks land on the
      // wrong index. 'y' makes the click hit the bar actually under the cursor.
      interaction: {
        mode: 'index' as const,
        intersect: false,
        axis: 'y' as const,
      },
      scales: {
        y: {
          type: 'category' as const,
          stacked: true,
        },
        x: {
          type: 'linear' as const,
          display: true,
          position: 'bottom' as const,
          stacked: true,
          title: { display: true, text: 'Count' },
          ticks: { precision: 0 },
        },
      },
    }),
    [onToggleErrorCode]
  )

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <Title level={5} className={styles.sectionTitle}>
          Top failure reasons — most frequent first
        </Title>
      </div>
      <Text type="secondary" className={styles.hint}>
        Click a bar to toggle filtering the recent failed attempts below.
      </Text>
      {errorBreakdownChartData ? (
        <div
          className={styles.chartWrap}
          style={{ height: errorBreakdownChartHeight }}
        >
          <Chart
            type="bar"
            data={errorBreakdownChartData}
            options={errorBreakdownChartOptions}
          />
        </div>
      ) : (
        <div className={styles.emptyState}>
          <Text type="secondary">No failures in this period.</Text>
        </div>
      )}
    </div>
  )
}
